import React, { useState, useEffect, useMemo } from 'react';
import { getGroupTitleById } from '../utils/groupTitles';
import './WordDetailsModal.css';

// Категории каузаторов как в WordCloud.jsx, чтобы маппить выбранные категории на реальные группы лемм
const CATEGORY_GROUPS = {
  'Желание': [
    { label: 'Чувствовать', key: 'чувствовать' },
    { label: 'Хотеть', key: 'хотеть' },
    { label: 'Перехотеть', key: 'перехотеть' },
    { label: 'Желать', key: 'желать' },
  ],
  'Потребность': [
    { label: 'Нуждаться', key: 'нуждаться' },
    { label: 'Требовать', key: 'требовать' },
    { label: 'Искать', key: 'искать' },
  ],
  'Надежда': [
    { label: 'Ждать', key: 'ждать' },
    { label: 'Верить', key: 'верить' },
    { label: 'Надеяться', key: 'надеяться' },
  ],
  'Страх': [
    { label: 'Бояться', key: 'бояться' },
  ],
  'Планирование': [
    { label: 'Пытаться', key: 'пытаться' },
    { label: 'Стараться', key: 'стараться' },
    { label: 'Планировать', key: 'планировать' },
    { label: 'Собираться', key: 'собираться' },
    { label: 'Готовиться', key: 'готовиться' },
    { label: 'Вздуматься', key: 'вздуматься' },
    { label: 'Решаться', key: 'решаться' },
    { label: 'Торопиться', key: 'торопиться' },
  ],
  'Результат усилий': [
    { label: 'Добиваться', key: 'добиваться' },
    { label: 'Справляться', key: 'справляться' },
    { label: 'Смочь', key: 'смочь' },
  ],
  'Страдание': [
    { label: 'Страдать', key: 'страдать' },
    { label: 'Переживать', key: 'переживать' },
    { label: 'Хренеть', key: 'хренеть' },
    { label: 'Уставать', key: 'уставать' },
    { label: 'Травиться', key: 'травиться' },
    { label: 'Жалеть', key: 'жалеть' },
    { label: 'Надоедать', key: 'надоедать' },
    { label: 'Плакать', key: 'плакать' },
    { label: 'Ныть', key: 'ныть' },
  ],
  'Позитивное отношение': [
    { label: 'Любить', key: 'любить' },
    { label: 'Нравиться', key: 'нравиться' },
    { label: 'Уважать', key: 'уважать' },
    { label: 'Гордиться', key: 'гордиться' },
  ],
  'Негативное отношение': [
    { label: 'Ненавидеть', key: 'ненавидеть' },
    { label: 'Завидовать', key: 'завидовать' },
    { label: 'Смеяться', key: 'смеяться' },
  ],
  'Пассивное отношение': [
    { label: 'Плевать', key: 'плевать' },
    { label: 'Терпеть', key: 'терпеть' },
    { label: 'Привыкать', key: 'привыкать' },
    { label: 'Отдыхать', key: 'отдыхать' },
  ],
  'Активное отношение': [
    { label: 'Будить', key: 'будить' },
    { label: 'Бороться', key: 'бороться' },
  ],
};

const WordDetailsModal = ({ isOpen, onClose, selectedWord, data = [], selectedGroups =[] }) => {
  const [currentPage, setCurrentPage] = useState(1);  
  const POSTS_PER_PAGE = 10;

  // Фильтруем данные по выбранному слову
  const filteredPosts = useMemo(() => {
    if (!selectedWord || !data || data.length === 0) return [];
  
    // Разворачиваем выбранные категории в набор predicate_group ключей
    const allowedPredicateGroups = new Set();
    if (Array.isArray(selectedGroups) && selectedGroups.length > 0) {
      selectedGroups.forEach(cat => {
        // Если пришли уже нижнего уровня ключи — тоже поддержим
        if (CATEGORY_GROUPS[cat]) {
          (CATEGORY_GROUPS[cat] || []).forEach(item => allowedPredicateGroups.add(item.key));
        } else {
          allowedPredicateGroups.add(cat);
        }
      });
    }
  
    const filtered = data.filter(item => {
      // 1️⃣ Фильтруем по выбранным группам
      if (selectedGroups.length > 0 && item.predicate_group) {
        if (!allowedPredicateGroups.has(item.predicate_group)) {
          return false;
        }
      }
  
      // 2️⃣ Проверяем наличие каузаторов
      if (!item.causators) return false;
  
      try {
        const causators = typeof item.causators === 'string'
          ? JSON.parse(item.causators)
          : item.causators;
  
        if (typeof causators === 'object' && causators !== null) {
          const lowerWord = selectedWord.toLowerCase();
  
          // ✅ Проверяем точное совпадение (а не просто includes)
          const inCausators =
            Object.keys(causators).some(
              key => key.toLowerCase() === lowerWord
            ) ||
            Object.values(causators).some(
              value => typeof value === 'string' && value.toLowerCase() === lowerWord
            );
  
          return inCausators;
        }
      } catch (error) {
        console.warn('Ошибка парсинга causators:', error);
      }
  
      return false;
    });
  
    // 3️⃣ Удаляем дубликаты по comment_id
    const uniquePosts = Array.from(
      new Map(filtered.map(item => [item.comment_id, item])).values()
    );
  
    return uniquePosts;
  }, [selectedWord, data, selectedGroups]);
  

  // Пагинация
  const totalPosts = filteredPosts.length;
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);  

  // Сбрасываем страницу при изменении слова
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedWord]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Неизвестно';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const highlightWord = (text, word) => {
    if (!text || !word) return text;
    const regex = new RegExp(`(${word})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  // Функция для извлечения и обработки текста из full_analysis
  const extractTextFromAnalysis = (fullAnalysis, selectedWord) => {
    if (!fullAnalysis) return null;
  
    try {
      const analysis = typeof fullAnalysis === 'string'
        ? JSON.parse(fullAnalysis)
        : fullAnalysis;
  
      const lowerWord = selectedWord?.toLowerCase().trim();
  
      const processText = (item) => {
        let mainText = item?.text || item?.marked_text || '';
        if (!mainText) return '';
  
        const clause = item?.clause || '';
        const predicate = item?.predicate_form || '';
  
        // 🔹 Экранируем спецсимволы для безопасных RegExp
        const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
        // 1️⃣ Подсветка clause и predicate
        if (clause) {
          const clauseRegex = new RegExp(escapeRegex(clause), 'i');
          mainText = mainText.replace(clauseRegex, `<span class="clause-highlight">${clause}</span>`);
        }
        if (predicate) {
          const predicateRegex = new RegExp(escapeRegex(predicate), 'i');
          mainText = mainText.replace(predicateRegex, `<span class="predicate-highlight">${predicate}</span>`);
        }
  
        // 2️⃣ Подсветка каузатора ТОЛЬКО внутри clause/predicate
        if (lowerWord) {
          // \b не работает с кириллицей → делаем вручную
          const wordRegex = new RegExp(`(?<!\\p{L})(${escapeRegex(lowerWord)})(?!\\p{L})`, 'gui');
  
          mainText = mainText.replace(
            /<span class="(clause-highlight|predicate-highlight)">([\s\S]*?)<\/span>/gi,
            (match, cls, inner) => {
              const highlightedInner = inner.replace(wordRegex, '<mark>$1</mark>');
              return `<span class="${cls}">${highlightedInner}</span>`;
            }
          );
        }
        // 2️⃣ Подсвечиваем каузатор ТОЛЬКО внутри clause или predicate
        if (selectedWord) {
          const escapedWord = selectedWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const wordRegex = new RegExp(`(?<![\\wа-яё])(${escapedWord})(?![\\wа-яё])`, 'gmi');
        
          // Подсвечиваем в пределах предложения после причины
          mainText = mainText.replace(
            /(<span class="(clause-highlight|predicate-highlight)">([\s\S]*?)<\/span>)([^.?!<]{0,30})/gi,
            (match, span, cls, inner, tail) => {
              // подсветить в самом блоке
              const highlightedInner = inner.replace(wordRegex, '<mark>$1</mark>');
              // и если каузатор встречается сразу после блока
              const highlightedTail = tail.replace(wordRegex, '<mark>$1</mark>');
              return `<span class="${cls}">${highlightedInner}</span>${highlightedTail}`;
            }
          );
        }
        

  
        return mainText;
      };
  
      if (Array.isArray(analysis)) {
        return processText(analysis[0]);
      } else if (typeof analysis === 'object') {
        return processText(analysis);
      }
  
      return null;
    } catch (error) {
      console.warn('Ошибка парсинга full_analysis:', error);
      return null;
    }
  };
  
  
  

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (!isOpen) return null;

  return (
    <div className="word-details-modal-overlay" onClick={onClose}>
      <div className="word-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="word-details-modal-header">
          <h3 className="word-details-modal-title">
            Посты и комментарии со словом: "{selectedWord}"
          </h3>
          <button
            onClick={onClose}
            className="word-details-modal-close"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        
        <div className="word-details-modal-content">
          {currentPosts.length === 0 ? (
            <div className="no-data-message">
              Не найдено постов и комментариев со словом "{selectedWord}"
            </div>
          ) : (
            <>
              <div className="posts-summary">
                Найдено постов: {totalPosts} (страница {currentPage} из {totalPages})
              </div>
              
              <div className="posts-list">
                {currentPosts.map((post, index) => (
                  <div key={post.id || index} className="post-item">
                    <div className="post-header">
                      <div className="post-meta">
                        <span className="post-date">
                          📅 {formatDate(post.date)}
                        </span>
                        {post.group_id && (
                          <span className="post-group">
                            👥 {getGroupTitleById(post.group_id) || `Группа ${post.group_id}`}
                          </span>
                        )}
                        {post.predicate_group && (
                          <span className="post-predicate-group">
                            🏷️ {post.predicate_group}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="post-content">
                    {(() => {
                      let postText = null;
                      if (post.full_analysis) {
                        postText = extractTextFromAnalysis(post.full_analysis, selectedWord);
                      } else if (post.text) {
                        postText = highlightWord(post.text, selectedWord);
                      }

                      return postText && (
                        <div className="post-text">
                          <strong>💬 Текст поста/комментария:</strong>
                          <div
                            className="post-text-content"
                            dangerouslySetInnerHTML={{ __html: postText }}
                          />
                        </div>
                      );
                    })()}


                      
                      {post.causators && (
                        <div className="post-causators">
                          <strong>🎯 Каузаторы:</strong>
                          <div className="causators-list">
                            {typeof post.causators === 'string' ? (
                              <span>{post.causators}</span>
                            ) : (
                              Object.entries(post.causators).map(([key, value]) => (
                                <span key={key} className="causator-item">
                                  {key}: {value}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                      
                      {post.analysis && (
                        <div className="post-analysis">
                          <strong>🔍 Анализ:</strong>
                          <div className="analysis-content">
                            {typeof post.analysis === 'string' ? (
                              <span>{post.analysis}</span>
                            ) : (
                              <pre>{JSON.stringify(post.analysis, null, 2)}</pre>
                            )}
                          </div>
                        </div>
                      )}                                                                  
                      {post.group_id && post.post_id && (
                        <div className="post-link">
                          <strong>📌 Ссылка на пост: </strong>
                          <a
                            href={`https://vk.com/wall${post.group_id}_${post.post_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="post-full-url"
                          >
                            https://vk.com/wall{post.group_id}_{post.post_id}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Пагинация */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    ← Предыдущая
                  </button>
                  
                  <span className="pagination-info">
                    Страница {currentPage} из {totalPages}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Следующая →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="word-details-modal-footer">
          <button
            onClick={onClose}
            className="word-details-modal-button"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default WordDetailsModal;
